import React, { useState } from "react";
import Layout from "@/components/Layout";
import usePageMeta from "@/hooks/use-page-meta";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Briefcase, Heart, GraduationCap, Users, Send } from "lucide-react";

const ROLES = [
  { title: "Critical Care Nurse", type: "Full-time · Delhi NCR", desc: "GNM / BSc with 2+ yrs ICU experience." },
  { title: "General Duty Assistant (GDA)", type: "Full-time · Delhi NCR", desc: "Certified GDAs with hospital experience." },
  { title: "Physiotherapist", type: "Part / Full-time · Delhi NCR", desc: "BPT / MPT with 1+ yr clinical experience." },
  { title: "Care Coordinator", type: "Full-time · Head Office", desc: "Excellent communication + clinical background preferred." },
];

const PERKS = [
  { icon: Heart, title: "Meaningful Work", desc: "Every shift touches a family's life." },
  { icon: GraduationCap, title: "Continuous Training", desc: "Regular clinical upskilling & certifications." },
  { icon: Users, title: "Supportive Team", desc: "Clinical supervisors always a call away." },
];

export default function CareersPage() {
  usePageMeta("Careers at KinFlex Home Healthcare — Nursing Jobs in Delhi NCR", "Join KinFlex Home Healthcare. Hiring critical care nurses, GDAs, physiotherapists & care coordinators across Delhi NCR.");
  const [form, setForm] = useState({ name: "", phone: "", email: "", role: "", qualification: "", experience: "", city: "", resume_url: "", message: "" });
  const [loading, setLoading] = useState(false);
  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.role || !form.message) { toast.error("Please share name, phone, role, and a brief note."); return; }
    setLoading(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (!payload[k]) delete payload[k]; });
      await api.post("/careers", payload);
      toast.success("Application received! Our HR team will call you soon.");
      setForm({ name: "", phone: "", email: "", role: "", qualification: "", experience: "", city: "", resume_url: "", message: "" });
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <Layout>
      <section className="pt-32 pb-12 md:pt-40 md:pb-16 bg-hero-gradient relative overflow-hidden">
        <div className="absolute inset-0 bg-medical-grid opacity-60 pointer-events-none" />
        <div className="container-page relative max-w-4xl">
          <div className="eyebrow mb-4">Careers</div>
          <h1 className="font-display font-bold text-slate-900 text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            Build a career in <span className="bg-gradient-to-r from-[#0A84FF] to-[#10B981] bg-clip-text text-transparent">home healthcare</span>.
          </h1>
          <p className="mt-6 text-lg text-slate-600 max-w-2xl">
            KinFlex is hiring compassionate, qualified healthcare professionals across Delhi NCR. Join a team that puts patients first.
          </p>
        </div>
      </section>

      <section className="section bg-white">
        <div className="container-page grid md:grid-cols-3 gap-5">
          {PERKS.map((p) => (
            <div key={p.title} className="rounded-2xl border border-slate-200/70 bg-white p-6">
              <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#0A84FF] to-[#10B981] text-white flex items-center justify-center">
                <p.icon className="h-5 w-5" />
              </div>
              <h3 className="mt-4 font-display font-semibold text-slate-900">{p.title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="section bg-[#F8FAFC]">
        <div className="container-page grid lg:grid-cols-2 gap-10 lg:gap-14">
          <div>
            <div className="eyebrow mb-3">Open Roles</div>
            <h2 className="font-display font-bold text-slate-900 text-3xl md:text-4xl tracking-tight">Current openings.</h2>
            <p className="mt-3 text-slate-600">Don't see your role? Send us your CV anyway — we hire year-round.</p>
            <div className="mt-6 space-y-3">
              {ROLES.map((r) => (
                <div key={r.title} className="rounded-2xl bg-white border border-slate-200/70 p-5 hover:border-[#0A84FF]/30 hover:shadow-md transition-all" data-testid={`role-${r.title.toLowerCase().replace(/\s/g, '-')}`}>
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(10, 132, 255, 0.1)' }}>
                      <Briefcase className="h-5 w-5" style={{ color: '#0A84FF' }} />
                    </div>
                    <div>
                      <div className="font-display font-semibold text-slate-900">{r.title}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{r.type}</div>
                      <div className="text-sm text-slate-600 mt-2 leading-relaxed">{r.desc}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <form onSubmit={submit} className="rounded-3xl bg-white border border-slate-200/70 p-6 md:p-8 shadow-sm" data-testid="careers-form">
            <h3 className="font-display font-bold text-slate-900 text-2xl">Apply Now</h3>
            <p className="text-sm text-slate-500 mt-1">Share your details — our HR team will reach out.</p>

            <div className="mt-6 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-semibold">Full Name *</Label>
                  <Input value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Your name" className="mt-1.5" required data-testid="careers-name" />
                </div>
                <div>
                  <Label className="text-xs font-semibold">Phone *</Label>
                  <Input value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+91 XXXXX XXXXX" className="mt-1.5" required data-testid="careers-phone" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-semibold">Email</Label>
                  <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@email.com" className="mt-1.5" data-testid="careers-email" />
                </div>
                <div>
                  <Label className="text-xs font-semibold">Role Applying For *</Label>
                  <Input value={form.role} onChange={(e) => update("role", e.target.value)} placeholder="e.g. Critical Care Nurse" className="mt-1.5" required data-testid="careers-role" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-semibold">Qualification</Label>
                  <Input value={form.qualification} onChange={(e) => update("qualification", e.target.value)} placeholder="GNM / BSc Nursing / BPT..." className="mt-1.5" data-testid="careers-qualification" />
                </div>
                <div>
                  <Label className="text-xs font-semibold">Experience</Label>
                  <Input value={form.experience} onChange={(e) => update("experience", e.target.value)} placeholder="e.g. 3 years" className="mt-1.5" data-testid="careers-experience" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-semibold">City</Label>
                  <Input value={form.city} onChange={(e) => update("city", e.target.value)} placeholder="Current city" className="mt-1.5" data-testid="careers-city" />
                </div>
                <div>
                  <Label className="text-xs font-semibold">Resume Link (Google Drive / URL)</Label>
                  <Input value={form.resume_url} onChange={(e) => update("resume_url", e.target.value)} placeholder="https://..." className="mt-1.5" data-testid="careers-resume" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold">About You *</Label>
                <Textarea value={form.message} onChange={(e) => update("message", e.target.value)} rows={4} placeholder="Key skills, past employers, why KinFlex..." className="mt-1.5" required data-testid="careers-message" />
              </div>
              <Button type="submit" disabled={loading} className="w-full rounded-full bg-[#0A84FF] hover:bg-[#0070d9] text-white h-12 font-semibold" data-testid="careers-submit">
                {loading ? "Submitting..." : <>Submit Application <Send className="h-4 w-4 ml-2" /></>}
              </Button>
            </div>
          </form>
        </div>
      </section>
    </Layout>
  );
}
